import { useEffect, useState } from "react";

import { FlowNavigation, FlowStep } from "./flow-step.types";

interface UseFlowNavigationArgs<Draft> {
  steps: FlowStep<Draft>[];
  draft: Draft;
  currentStep: number;
  setCurrentStep: (index: number) => void;
}

function furthestReachableStep<Draft>(steps: FlowStep<Draft>[], draft: Draft): number {
  let furthest = 0;
  for (let index = 0; index < steps.length; index++) {
    if (!steps[index].isReachable(draft)) {
      break;
    }
    furthest = index;
  }
  return furthest;
}

export function useFlowNavigation<Draft>({
  steps,
  draft,
  currentStep,
  setCurrentStep,
}: UseFlowNavigationArgs<Draft>): FlowNavigation {
  const [blockedStep, setBlockedStep] = useState<number | undefined>(undefined);

  if (steps.length === 0) {
    throw new Error("useFlowNavigation requires at least one step");
  }

  const furthest = furthestReachableStep(steps, draft);
  const clampedStep = Math.min(Math.max(currentStep, 0), furthest);
  const activeStep = steps[clampedStep];
  const gateMessage = activeStep.validate(draft);
  const errorMessage = blockedStep === clampedStep ? gateMessage : undefined;

  useEffect(() => {
    if (!gateMessage) {
      setBlockedStep(undefined);
    }
  }, [gateMessage]);

  const goNext = () => {
    if (gateMessage) {
      setBlockedStep(clampedStep);
      return;
    }
    setBlockedStep(undefined);
    if (clampedStep < steps.length - 1) {
      setCurrentStep(clampedStep + 1);
    }
  };

  const goBack = () => {
    if (clampedStep > 0) {
      setBlockedStep(undefined);
      setCurrentStep(clampedStep - 1);
    }
  };

  const goToStep = (index: number) => {
    const target = Math.min(Math.max(index, 0), furthest);
    setBlockedStep(undefined);
    setCurrentStep(target);
  };

  return {
    currentStep: clampedStep,
    currentStepId: activeStep.id,
    stepLabels: steps.map((step) => step.label),
    canGoBack: clampedStep > 0,
    errorMessage,
    goNext,
    goBack,
    goToStep,
  };
}
